import React from 'react';
import { CheckCircle2, ClipboardCheck } from 'lucide-react';
import { Accordion } from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { PermissionCard, SubPermissionCard, SectionAccordionItem } from '../cards';
import { BulkActionsDropdown } from '../BulkActionsDropdown';

export interface ApprovalTabProps {
  viewApprovalDashboard: boolean;
  onViewApprovalDashboardChange: (value: boolean) => void;
  viewApprovals: boolean;
  onViewApprovalsChange: (value: boolean) => void;
  editTimesheetsApproval: boolean;
  onEditTimesheetsApprovalChange: (value: boolean) => void;
  rejectTimesheets: boolean;
  onRejectTimesheetsChange: (value: boolean) => void;
  compulsoryRejectionReason: boolean;
  onCompulsoryRejectionReasonChange: (value: boolean) => void;
  bulkApprove: boolean;
  onBulkApproveChange: (value: boolean) => void;
  delegateApproval: boolean;
  onDelegateApprovalChange: (value: boolean) => void;
  viewPolicyViolations: boolean;
  onViewPolicyViolationsChange: (value: boolean) => void;
  viewDetectedIssues: boolean;
  onViewDetectedIssuesChange: (value: boolean) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

export const ApprovalTab: React.FC<ApprovalTabProps> = ({
  viewApprovalDashboard,
  onViewApprovalDashboardChange,
  viewApprovals,
  onViewApprovalsChange,
  editTimesheetsApproval,
  onEditTimesheetsApprovalChange,
  rejectTimesheets,
  onRejectTimesheetsChange,
  compulsoryRejectionReason,
  onCompulsoryRejectionReasonChange,
  bulkApprove,
  onBulkApproveChange,
  delegateApproval,
  onDelegateApprovalChange,
  viewPolicyViolations,
  onViewPolicyViolationsChange,
  viewDetectedIssues,
  onViewDetectedIssuesChange,
  onSelectAll,
  onDeselectAll,
}) => (
  <>
    <BulkActionsDropdown onSelectAll={onSelectAll} onDeselectAll={onDeselectAll} />
    <Accordion type="multiple" defaultValue={['approval-homepage', 'timesheet-approval']} className="space-y-3">

      <SectionAccordionItem value="approval-homepage" icon={<CheckCircle2 className="h-5 w-5" />} title="Approval Homepage" description="Access to approval homepage and metrics">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <PermissionCard
            label={<Label htmlFor="view-homepage" className="cursor-pointer">View Homepage</Label>}
            description="View approval homepage with metrics and action items"
            onToggle={() => onViewApprovalDashboardChange(!viewApprovalDashboard)}
            control={<Checkbox id="view-homepage" checked={viewApprovalDashboard} onCheckedChange={(c) => onViewApprovalDashboardChange(c as boolean)} />}
          />
        </div>
      </SectionAccordionItem>

      <SectionAccordionItem value="timesheet-approval" icon={<ClipboardCheck className="h-5 w-5" />} title="Timesheet Approval" description="View and manage timesheet approvals">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
          <PermissionCard
            label={<Label htmlFor="view-approvals" className="cursor-pointer">View Approvals</Label>}
            description="View timesheet approvals"
            onToggle={() => onViewApprovalsChange(!viewApprovals)}
            control={
              <Checkbox
                id="view-approvals"
                checked={viewApprovals}
                onCheckedChange={(c) => onViewApprovalsChange(c as boolean)}
              />
            }
          />
          <PermissionCard
            label={<Label htmlFor="edit-timesheets-approval" className="cursor-pointer">Edit Timesheets</Label>}
            description="Modify time entries while reviewing for approval"
            onToggle={() => onEditTimesheetsApprovalChange(!editTimesheetsApproval)}
            control={
              <Checkbox
                id="edit-timesheets-approval"
                checked={editTimesheetsApproval}
                onCheckedChange={(c) => onEditTimesheetsApprovalChange(c as boolean)}
              />
            }
          />
          <div className="flex flex-col gap-2">
            <PermissionCard
              label={<Label htmlFor="reject-timesheets" className="cursor-pointer">Reject Timesheets</Label>}
              description="Reject timesheets"
              onToggle={() => onRejectTimesheetsChange(!rejectTimesheets)}
              control={
                <Checkbox
                  id="reject-timesheets"
                  checked={rejectTimesheets}
                  onCheckedChange={(c) => onRejectTimesheetsChange(c as boolean)}
                />
              }
            />
            {rejectTimesheets && (
              <SubPermissionCard
                label="Compulsory Rejection Reason"
                description="Force reason for rejection"
                onToggle={() => onCompulsoryRejectionReasonChange(!compulsoryRejectionReason)}
                control={
                  <Checkbox
                    id="compulsory-rejection"
                    checked={compulsoryRejectionReason}
                    onCheckedChange={(c) => onCompulsoryRejectionReasonChange(c as boolean)}
                  />
                }
              />
            )}
          </div>
          <PermissionCard
            label={<Label htmlFor="bulk-approve" className="cursor-pointer">Bulk Approve & Reject</Label>}
            description="Approve or reject multiple timesheets at once"
            onToggle={() => onBulkApproveChange(!bulkApprove)}
            control={
              <Checkbox
                id="bulk-approve"
                checked={bulkApprove}
                onCheckedChange={(c) => onBulkApproveChange(c as boolean)}
              />
            }
          />
          <PermissionCard
            label={<Label htmlFor="delegate-approval" className="cursor-pointer">Delegate Approval</Label>}
            description="Allow this user to delegate their approval responsibilities to another user"
            onToggle={() => onDelegateApprovalChange(!delegateApproval)}
            control={
              <Checkbox
                id="delegate-approval"
                checked={delegateApproval}
                onCheckedChange={(c) => onDelegateApprovalChange(c as boolean)}
              />
            }
          />
          <PermissionCard
            label={<Label htmlFor="view-policy-violations" className="cursor-pointer">View Policy Violations</Label>}
            description="View policy violations"
            onToggle={() => onViewPolicyViolationsChange(!viewPolicyViolations)}
            control={
              <Checkbox
                id="view-policy-violations"
                checked={viewPolicyViolations}
                onCheckedChange={(c) => onViewPolicyViolationsChange(c as boolean)}
              />
            }
          />
          <PermissionCard
            label={<Label htmlFor="view-detected-issues" className="cursor-pointer">View Detected Issues</Label>}
            description="View detected issues"
            onToggle={() => onViewDetectedIssuesChange(!viewDetectedIssues)}
            control={<Checkbox id="view-detected-issues" checked={viewDetectedIssues} onCheckedChange={(c) => onViewDetectedIssuesChange(c as boolean)} />}
          />
        </div>
      </SectionAccordionItem>
    </Accordion>
  </>
);
